import { useTranslation } from 'react-i18next';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

export default function ContactPage() {
  const { t } = useTranslation();

  useSEO({
    title: 'İletişim | Canel Elektrik – İstanbul Elektrik ve İnşaat Taahhüt',
    description:
      "Canel Elektrik ile iletişime geçin. İstanbul Perpa'daki ofisimizden endüstriyel elektrik mühendisliği ve anahtar teslim inşaat projeleri için teklif alın. Tel: +90 212 220 52 56",
    keywords:
      'Canel Elektrik iletişim, elektrik taahhüt teklif İstanbul, Perpa Ticaret Merkezi, endüstriyel proje teklif',
    canonical: '/contact',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'İletişim – Canel Elektrik İnşaat Taahhüt Mühendislik',
      url: `${SITE_URL}/contact`,
      description: 'Elektrik ve inşaat taahhüt projeleriniz için Canel Elektrik ile iletişime geçin.',
      mainEntity: {
        '@type': 'LocalBusiness',
        name: 'Canel Elektrik İnşaat Taahhüt Mühendislik',
        telephone: '+90-212-220-52-56',
        email: 'info@can-el.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Perpa Ticaret Merkezi, A Blok Kat: 5 No: 204',
          addressLocality: 'Okmeydanı, Şişli',
          addressRegion: 'İstanbul',
          postalCode: '34384',
          addressCountry: 'TR',
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: 'Saturday',
            opens: '09:00',
            closes: '12:00',
          },
        ],
      },
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-56 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Istanbul%20Turkey%20Perpa%20business%20center%20office%20building%20exterior%20modern%20architecture%20professional&width=1920&height=400&seq=contact-hero&orientation=landscape"
          alt="Contact"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A2332]/80 to-[#1A2332]/40" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-16 max-w-7xl mx-auto w-full">
          <span className="text-[#E8A020] text-xs font-bold tracking-widest uppercase mb-3">{t('contact_label')}</span>
          <h1 className="text-3xl md:text-4xl font-black text-white">{t('contact_title')}</h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <h2 className="text-2xl font-black text-[#1A2332] mb-4">{t('contact_label')}</h2>
            <p className="text-gray-500 text-base leading-relaxed">{t('contact_desc')}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
            <div className="bg-[#F8F9FA] rounded-lg p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-[#E8A020]/10 rounded-lg mb-4">
                <i className="ri-map-pin-line text-[#E8A020] text-xl" />
              </div>
              <div className="font-bold text-[#1A2332] text-sm mb-2">{t('contact_address_label')}</div>
              <div className="text-gray-500 text-sm whitespace-pre-line leading-relaxed">{t('contact_address_val')}</div>
            </div>

            <div className="bg-[#F8F9FA] rounded-lg p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-[#E8A020]/10 rounded-lg mb-4">
                <i className="ri-phone-line text-[#E8A020] text-xl" />
              </div>
              <div className="font-bold text-[#1A2332] text-sm mb-2">{t('contact_phone_label')}</div>
              <a href="tel:+902122205256" className="block text-gray-500 text-sm hover:text-[#E8A020] transition-colors">+90 212 220 52 56</a>
              <a href="tel:+905533585024" className="block text-gray-500 text-sm mt-1 hover:text-[#E8A020] transition-colors">+90 553 358 50 24</a>
            </div>

            <div className="bg-[#F8F9FA] rounded-lg p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-[#E8A020]/10 rounded-lg mb-4">
                <i className="ri-mail-line text-[#E8A020] text-xl" />
              </div>
              <div className="font-bold text-[#1A2332] text-sm mb-2">{t('contact_email_label')}</div>
              <a href="mailto:info@can-el.com" className="text-gray-500 text-sm hover:text-[#E8A020] transition-colors">info@can-el.com</a>
            </div>

            <div className="bg-[#F8F9FA] rounded-lg p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-[#E8A020]/10 rounded-lg mb-4">
                <i className="ri-time-line text-[#E8A020] text-xl" />
              </div>
              <div className="font-bold text-[#1A2332] text-sm mb-2">{t('contact_hours_label')}</div>
              <div className="text-gray-500 text-sm">{t('contact_hours_weekdays')}</div>
              <div className="text-gray-500 text-sm mt-1">{t('contact_hours_saturday')}</div>
            </div>
          </div>

          {/* Map */}
          <div className="w-full h-80 rounded-lg overflow-hidden border border-gray-100 relative">
            <iframe
              title="Canel Elektrik Konum"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1548.2743241920389!2d28.969340986029714!3d41.06033849413451!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab7ee13e4128f%3A0x301c941e52d55560!2zQ2FuZWwgRWxla3RyaWsgxLBuxZ9hYXQgVGFhaGjDvHQgTcO8aGVuZGlzbGlr!5e0!3m2!1str!2str!4v1778746754561!5m2!1str!2str"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
            />
            <a
              href="https://maps.app.goo.gl/1PBxXo2VDwJVQxSg6"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-3 right-3 bg-white text-[#1A2332] text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-200 flex items-center gap-1.5 hover:bg-[#E8A020] hover:text-white hover:border-[#E8A020] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-map-pin-2-line" />
              {t('get_directions')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}